import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ClientDetailsModal = ({ isOpen, onClose, client, formatCurrency, formatDate }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !client) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'cases', label: 'Cases', icon: 'Briefcase' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'billing', label: 'Billing', icon: 'CreditCard' },
    { id: 'communications', label: 'Communications', icon: 'MessageSquare' }
  ];

  const mockCases = [
    {
      id: 1,
      title: "Property Dispute - Kiambu Land",
      caseNumber: "PROP/2024/001",
      status: "active",
      category: "Property Law",
      dateOpened: new Date('2024-01-10'),
      nextHearing: new Date('2024-02-15'),
      description: "Land ownership dispute involving 2-acre plot in Kiambu County"
    },
    {
      id: 2,
      title: "Employment Contract Review",
      caseNumber: "EMP/2024/002",
      status: "active",
      category: "Employment Law",
      dateOpened: new Date('2024-01-05'),
      nextHearing: null,
      description: "Review and negotiation of employment contract terms"
    }
  ];

  const mockDocuments = [
    {
      id: 1,
      name: "Client Intake Form.pdf",
      type: "application/pdf",
      size: "245 KB",
      uploadDate: new Date('2024-01-10'),
      category: "Client Documents"
    },
    {
      id: 2,
      name: "Property Title Deed.pdf",
      type: "application/pdf",
      size: "1.2 MB",
      uploadDate: new Date('2024-01-12'),
      category: "Case Documents"
    }
  ];

  const mockBillingHistory = [
    {
      id: 1,
      invoiceNumber: "INV-2024-001",
      date: new Date('2024-01-15'),
      amount: 150000,
      status: "paid",
      description: "Legal consultation and case preparation"
    },
    {
      id: 2,
      invoiceNumber: "INV-2024-002",
      date: new Date('2024-01-30'),
      amount: 200000,
      status: "pending",
      description: "Court representation and document preparation"
    }
  ];

  const mockCommunications = [
    {
      id: 1,
      type: "email",
      subject: "Case Update - Property Dispute",
      date: new Date('2024-01-15'),
      direction: "outgoing",
      content: "Dear Mr. Kamau, I wanted to update you on the progress of your property dispute case..."
    },
    {
      id: 2,
      type: "phone",
      subject: "Phone consultation",
      date: new Date('2024-01-12'),
      direction: "incoming",
      duration: "25 minutes",
      content: "Discussed case strategy and next steps for the property dispute"
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-success-100', text: 'text-success-700', label: 'Active' },
      pending: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'Pending' },
      paid: { bg: 'bg-success-100', text: 'text-success-700', label: 'Paid' },
      overdue: { bg: 'bg-error-100', text: 'text-error-700', label: 'Overdue' },
      closed: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Closed' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Client Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-text-primary">Contact Information</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Icon name="Mail" size={16} className="text-text-secondary" />
              <span className="text-text-primary">{client.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="Phone" size={16} className="text-text-secondary" />
              <span className="text-text-primary">{client.phone}</span>
            </div>
            {client.mpesaNumber && (
              <div className="flex items-center gap-3">
                <Icon name="Smartphone" size={16} className="text-success-600" />
                <span className="text-text-primary">M-Pesa: {client.mpesaNumber}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Icon name="MapPin" size={16} className="text-text-secondary" />
              <span className="text-text-primary">{client.county}</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-text-primary">Client Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-text-secondary">Client Type:</span>
              <span className="text-text-primary capitalize">{client.clientType}</span>
            </div>
            {client.businessRegNumber && (
              <div className="flex justify-between">
                <span className="text-text-secondary">Business Reg:</span>
                <span className="text-text-primary">{client.businessRegNumber}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-text-secondary">Portal Access:</span>
              <span className={`text-sm ${client.portalAccess ? 'text-success-600' : 'text-text-secondary'}`}>
                {client.portalAccess ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Status:</span>
              {getStatusBadge(client.relationshipStatus)}
            </div>
          </div>
        </div>
      </div>

      {/* Address */}
      {client.address && (
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-3">Address</h3>
          <div className="bg-secondary-50 p-4 rounded-lg">
            <pre className="text-text-primary whitespace-pre-wrap font-sans">{client.address}</pre>
          </div>
        </div>
      )}

      {/* Notes */}
      {client.notes && (
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-3">Notes</h3>
          <div className="bg-secondary-50 p-4 rounded-lg">
            <p className="text-text-primary">{client.notes}</p>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-primary-50 p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="Briefcase" size={20} className="text-primary" />
            <div>
              <div className="text-2xl font-bold text-primary">{client.activeCases}</div>
              <div className="text-sm text-text-secondary">Active Cases</div>
            </div>
          </div>
        </div>
        <div className="bg-success-50 p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="DollarSign" size={20} className="text-success-600" />
            <div>
              <div className="text-2xl font-bold text-success-600">{formatCurrency(client.totalBillings)}</div>
              <div className="text-sm text-text-secondary">Total Billings</div>
            </div>
          </div>
        </div>
        <div className="bg-warning-50 p-4 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="Calendar" size={20} className="text-warning-600" />
            <div>
              <div className="text-2xl font-bold text-warning-600">{formatDate(client.lastContact)}</div>
              <div className="text-sm text-text-secondary">Last Contact</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCases = () => (
    <div className="space-y-4">
      {mockCases.map(caseItem => (
        <div key={caseItem.id} className="border border-border rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold text-text-primary">{caseItem.title}</h4>
              <p className="text-sm text-text-secondary">{caseItem.caseNumber}</p>
            </div>
            {getStatusBadge(caseItem.status)}
          </div>
          <p className="text-text-secondary mb-3">{caseItem.description}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-text-secondary">Category:</span>
              <span className="text-text-primary ml-2">{caseItem.category}</span>
            </div>
            <div>
              <span className="text-text-secondary">Date Opened:</span>
              <span className="text-text-primary ml-2">{formatDate(caseItem.dateOpened)}</span>
            </div>
            <div>
              <span className="text-text-secondary">Next Hearing:</span>
              <span className="text-text-primary ml-2">
                {caseItem.nextHearing ? formatDate(caseItem.nextHearing) : 'Not scheduled'}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-4">
      {mockDocuments.map(doc => (
        <div key={doc.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={20} className="text-text-secondary" />
            <div>
              <h4 className="font-medium text-text-primary">{doc.name}</h4>
              <p className="text-sm text-text-secondary">
                {doc.category} • {doc.size} • {formatDate(doc.uploadDate)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-lg transition-smooth">
              <Icon name="Download" size={16} />
            </button>
            <button className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-lg transition-smooth">
              <Icon name="Eye" size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderBilling = () => (
    <div className="space-y-4">
      {mockBillingHistory.map(bill => (
        <div key={bill.id} className="border border-border rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold text-text-primary">{bill.invoiceNumber}</h4>
              <p className="text-sm text-text-secondary">{bill.description}</p>
            </div>
            <div className="text-right">
              <div className="font-semibold text-text-primary">{formatCurrency(bill.amount)}</div>
              {getStatusBadge(bill.status)}
            </div>
          </div>
          <div className="text-sm text-text-secondary">
            Date: {formatDate(bill.date)}
          </div>
        </div>
      ))}
    </div>
  );

  const renderCommunications = () => (
    <div className="space-y-4">
      {mockCommunications.map(comm => (
        <div key={comm.id} className="border border-border rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <Icon 
                name={comm.type === 'email' ? 'Mail' : 'Phone'} 
                size={16} 
                className="text-text-secondary" 
              />
              <div>
                <h4 className="font-medium text-text-primary">{comm.subject}</h4>
                <p className="text-sm text-text-secondary">
                  {comm.direction === 'outgoing' ? 'Sent' : 'Received'} • {formatDate(comm.date)}
                  {comm.duration && ` • ${comm.duration}`}
                </p>
              </div>
            </div>
          </div>
          <p className="text-text-secondary">{comm.content}</p>
        </div>
      ))}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'cases':
        return renderCases();
      case 'documents':
        return renderDocuments();
      case 'billing':
        return renderBilling();
      case 'communications':
        return renderCommunications();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg shadow-dialog w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-secondary-200">
              <Image
                src={client.avatar}
                alt={client.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary">{client.name}</h2>
              <p className="text-text-secondary">{client.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth focus:outline-none focus:ring-2 focus:ring-secondary-400"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ClientDetailsModal;